import type { ModalSheetConfig } from 'src/components/contexts/ModalSheetContext'
import { useModalSheet } from 'src/components/contexts/ModalSheetContext'
import { useProductPageContext } from 'src/components/restructure/product/contexts'
import { InView } from 'react-intersection-observer'
import { Image } from 'src/components/ui/Image'
import { useMobile } from 'src/hooks/useMobile'
import { RulerIcon } from 'src/images/icons/RulerIcon'
import { formatTableData } from 'src/utils/pdp/formatTableData'
import { sendProductInformationSelectedEvent } from 'src/utils/amplitude/sendProductInformationSelectedEvent'
import { getStandardObject } from 'src/utils/amplitude/getStandardObject'
import { getProductQueryObject } from 'src/utils/amplitude/useProductObject'
import { getFeatureObject } from 'src/utils/amplitude/getFeatureObject'
import { dispatchAmplitudeEvent } from 'src/utils/amplitude'
import './styles.scss'

export function SizeGuide() {
  const { isMobile } = useMobile()
  const { openModal } = useModalSheet()
  const { product, currentSku } = useProductPageContext()
  const standardObject = getStandardObject()
  const productObject = getProductQueryObject(currentSku)
  const featureDetailsObject = getFeatureObject({
    locationOnPage: null,
    name: 'Tabela de tamanhos',
    section: 'produto',
  })

  if (!product.sizeDetails) {
    return null
  }

  const {
    how_to_measure: { title, subtitle, description, imageUrl },
    how_to_measure_details: howToMeasureDetails,
    conception_sizes_details: {
      conception_size_parameters: conceptionSizeParameters,
    },
    recommendation,
  } = product.sizeDetails

  const { tableHeaderX, tableHeaderY } = formatTableData(
    conceptionSizeParameters
  )

  const ModalContent = () => (
    <div className="m-6 mt-0">
      {recommendation && (
        <div className="bg-neutral02 p-4 flex justify-between gap-3">
          <div className="h-5 w-5">
            <RulerIcon />
          </div>
          <span className="text-sm">{recommendation}</span>
        </div>
      )}

      <div className="size-guide-table-wrapper my-6 overflow-x-auto w-full">
        <table className="size-guide-table text-sm w-full mb-8">
          <thead>
            <tr>
              <th className="w-[113px]"> </th>
              {tableHeaderX.map((label, i) => (
                <th key={`${label}-${i}`} className="font-bold w-[152px]">
                  {label}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {tableHeaderY.map((label, i) => {
              const rowData = conceptionSizeParameters.map(
                ({ sizes }) => sizes.filter((size) => size.label === label)[0]
              )

              return (
                <tr
                  key={`${label}-${i}`}
                  className="last:border-b-0 border-y border-gray"
                >
                  <td>{label}</td>
                  {rowData.map(({ value }) => (
                    <td key={`${value}`}>{value}</td>
                  ))}
                </tr>
              )
            })}
          </tbody>
        </table>
      </div>

      {tableHeaderX.length > 2 && (
        <span className="text-tertiary text-sm mb-10">
          Arraste para o lado para ver mais
        </span>
      )}

      <div className="mt-12">
        <span className="text-xl sm:text-2xl font-medium">{subtitle}</span>
        <p className="mt-4 mb-10 text-secondary">{description}</p>

        <Image
          src={imageUrl}
          width={isMobile ? 327 : 437}
          height={isMobile ? 327 : 437}
          alt="Como medir"
        />

        <div className="mt-10">
          {howToMeasureDetails.map(({ label, value }, i) => (
            <p key={`${label}-${i}`} className="first:mt-0 last:mb-0 my-5">
              <b>{label}:</b> {value}
            </p>
          ))}
        </div>
      </div>
    </div>
  )

  const modalConfig: ModalSheetConfig = {
    content: <ModalContent />,
    title,
    height: '90vh',
  }

  const sendPromoFeatureViewedEvent = () => {
    const eventData = {
      ...getStandardObject(),
      ...featureDetailsObject,
    }

    dispatchAmplitudeEvent({ eventName: 'Product Feature Viewed', eventData })
  }

  return (
    <InView
      key={product.productId}
      as="div"
      threshold={0.7}
      triggerOnce
      onChange={(inView) => {
        if (inView) {
          sendPromoFeatureViewedEvent()
        }
      }}
    >
      <button
        className="desktop-caption-regular text-restructure-tertiary underline cursor-pointer flex items-center gap-1"
        onClick={() => {
          sendProductInformationSelectedEvent({
            standardObject,
            productObject,
            clickedElement: 'Tabela de tamanhos',
            featureDetailsObject,
          })

          openModal(modalConfig)
        }}
      >
        Tabela de tamanhos
      </button>
    </InView>
  )
}
